import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import { supabase } from '../../lib/supabase';
import { queueMessage } from '../../lib/indexedDBQueue';
import Icon from 'components/AppIcon';
import MainSidebar from 'components/MainSidebar';
import MessageBubble from './components/MessageBubble';
import ActivityFeedItem from './components/ActivityFeedItem';
import NotificationPanel from './components/NotificationPanel';



const PAGE_SIZE = 50;

// ── Chat Error Boundary ───────────────────────────────────────
class ChatErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={28} className="text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Chat Error</h3>
            <p className="text-sm text-muted-foreground mb-4">{this.state?.error?.message || 'An unexpected error occurred in faction chat.'}</p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location?.reload(); }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Reload Chat
            </button>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── Connection Status Indicator ───────────────────────────────
const ConnectionStatus = ({ status, onlineCount }) => {
  const config = {
    connected: { color: 'bg-emerald-400', text: 'Connected', textColor: 'text-emerald-400' },
    connecting: { color: 'bg-yellow-400 animate-pulse', text: 'Reconnecting...', textColor: 'text-yellow-400' },
    error: { color: 'bg-yellow-400 animate-pulse', text: 'Reconnecting...', textColor: 'text-yellow-400' },
    disconnected: { color: 'bg-red-400', text: 'Disconnected', textColor: 'text-red-400' },
    idle: { color: 'bg-muted-foreground', text: 'Connecting...', textColor: 'text-muted-foreground' },
  };
  const cfg = config?.[status] || config?.idle;
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${cfg?.color}`} />
        <span className={`text-xs ${cfg?.textColor}`}>{cfg?.text}</span>
      </div>
      {onlineCount > 0 && (
        <span className="flex items-center gap-1 text-xs text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          {onlineCount} online
        </span>
      )}
    </div>
  );
};

// ── Typing Indicator ─────────────────────────────────────────
const TypingIndicator = ({ typingUsers }) => {
  if (!typingUsers || typingUsers?.length === 0) return null;
  const names = typingUsers?.slice(0, 3)?.map(u => u?.username || 'Someone');
  const label = names?.length === 1
    ? `${names?.[0]} is typing...`
    : names?.length === 2
    ? `${names?.[0]} and ${names?.[1]} are typing...`
    : `${names?.[0]}, ${names?.[1]} and ${names?.length - 2} more are typing...`;
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground">
      <span className="flex gap-0.5">
        {[0, 1, 2]?.map(i => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce"
            style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </span>
      <span>{label}</span>
    </div>
  );
};

// ── Online Members List ───────────────────────────────────────
const OnlineMemberDot = ({ username, isOnline }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs">
    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
      isOnline ? 'bg-emerald-400' : 'bg-muted-foreground/40'
    }`} />
    <span className="text-foreground truncate max-w-[80px]">{username}</span>
  </div>
);

// ── Vault Balance Banner ──────────────────────────────────────
const VaultBanner = ({ vaultBalance, vaultUpdated }) => {
  if (vaultBalance === null || vaultBalance === undefined) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card/30 flex-shrink-0">
      <Icon name="Vault" size={13} className="text-yellow-400" />
      <span className="text-xs text-muted-foreground">Faction Vault:</span>
      <span className="text-xs font-semibold text-yellow-400">${Number(vaultBalance)?.toLocaleString()}</span>
      {vaultUpdated && (
        <span className="text-xs text-muted-foreground ml-1">· Updated just now</span>
      )}
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────
const ChatEmptyState = () => (
  <div className="flex-1 flex items-center justify-center py-16">
    <div className="text-center max-w-xs">
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <Icon name="MessageSquare" size={36} className="text-primary/60" />
      </div>
      <h3 className="text-base font-semibold text-foreground mb-2">No messages yet</h3>
      <p className="text-sm text-muted-foreground">No messages — start the conversation!</p>
    </div>
  </div>
);

// ── Failed Message Item ───────────────────────────────────────
const FailedMessageItem = ({ msg, onRetry, onDismiss }) => (
  <div className="flex gap-2 px-4 py-2">
    <div className="flex-1 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
      <p className="text-sm text-foreground/70 line-through">{msg?.message_text}</p>
      <p className="text-xs text-red-400 mt-0.5">Failed to send</p>
    </div>
    <div className="flex flex-col gap-1">
      <button
        onClick={() => onRetry(msg)}
        className="px-2 py-1 rounded bg-primary/10 text-primary text-xs hover:bg-primary/20 transition-colors min-h-[32px]"
      >
        Retry
      </button>
      <button
        onClick={() => onDismiss(msg?.id)}
        className="px-2 py-1 rounded bg-muted text-muted-foreground text-xs hover:bg-muted/80 transition-colors min-h-[32px]"
      >
        Dismiss
      </button>
    </div>
  </div>
);

const FactionChat = () => {
  const [messages, setMessages] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userFaction, setUserFaction] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePanel, setActivePanel] = useState('chat');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [failedMessages, setFailedMessages] = useState([]);
  const [messageError, setMessageError] = useState('');
  // Presence & typing
  const [onlineMembers, setOnlineMembers] = useState({});
  const [typingUsers, setTypingUsers] = useState([]);
  // Vault
  const [vaultBalance, setVaultBalance] = useState(null);
  const [vaultUpdated, setVaultUpdated] = useState(false);
  // Message delivery status: { [optimisticId]: 'sent' | 'delivered' | 'failed' }
  const [deliveryStatus, setDeliveryStatus] = useState({});
  // Mention notifications
  const [mentionNotifs, setMentionNotifs] = useState([]);

  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const presenceChannelRef = useRef(null);
  const vaultChannelRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);
  const vaultUpdatedTimerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ── Keyboard handling: scroll input into view ─────────────
  useEffect(() => {
    const handleViewportResize = () => {
      if (document.activeElement === inputRef?.current) {
        setTimeout(() => {
          inputRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    };
    if (window.visualViewport) {
      window.visualViewport?.addEventListener('resize', handleViewportResize);
      return () => window.visualViewport?.removeEventListener('resize', handleViewportResize);
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      setCurrentUser(user);

      if (!user) { setLoading(false); return; }

      const { data: memberData } = await supabase?.from('faction_members')?.select('faction_id, rank')?.eq('user_id', user?.id)?.single();
      if (!memberData) { setLoading(false); return; }

      setUserRank(memberData?.rank);

      const { data: factionData } = await supabase?.from('factions')?.select('*')?.eq('id', memberData?.faction_id)?.single();
      setUserFaction(factionData);

      const { data: msgs } = await supabase?.from('faction_messages')?.select('*, user_profiles(username)')?.eq('faction_id', memberData?.faction_id)?.eq('is_deleted', false)?.order('created_at', { ascending: true })?.limit(PAGE_SIZE);
      setMessages(msgs || []);

      const { data: activity } = await supabase?.from('faction_activity_feed')?.select('*, user_profiles(username)')?.eq('faction_id', memberData?.faction_id)?.order('created_at', { ascending: false })?.limit(20);
      setActivityFeed(activity || []);

      if (['LEADER', 'OFFICER']?.includes(memberData?.rank)) {
        const { data: notifs } = await supabase?.from('faction_notifications')?.select('*')?.eq('target_user_id', user?.id)?.order('created_at', { ascending: false })?.limit(20);
        setNotifications(notifs || []);
      }

      // Fetch vault balance
      const { data: vaultData } = await supabase?.from('faction_vaults')?.select('balance')?.eq('faction_id', memberData?.faction_id)?.single();
      if (vaultData) setVaultBalance(vaultData?.balance);

    } catch (err) {
      setError(err?.message || 'Failed to load faction chat');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ── Real-time message + activity subscription ─────────────
  useEffect(() => {
    if (!userFaction?.id) return;

    setConnectionStatus('connecting');
    console.log('[FactionChat] Subscribing to faction:', userFaction?.id);

    const channel = supabase?.channel(`faction-chat-${userFaction?.id}`)
      ?.on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'faction_messages',
        filter: `faction_id=eq.${userFaction?.id}`,
      }, async (payload) => {
        console.log('[FactionChat] New message received:', payload?.new?.id);
        const { data: msgWithProfile } = await supabase?.from('faction_messages')?.select('*, user_profiles(username)')?.eq('id', payload?.new?.id)?.single();
        if (msgWithProfile) {
          setMessages((prev) => {
            if (prev?.some((m) => m?.id === msgWithProfile?.id)) return prev;
            // Remove matching optimistic message
            const filtered = prev?.filter(m => !(
              m?._optimistic &&
              m?.sender_id === msgWithProfile?.sender_id &&
              m?.message_text === msgWithProfile?.message_text
            ));
            return [...filtered, msgWithProfile];
          });
          // Mark delivered for own messages
          if (msgWithProfile?.sender_id === currentUser?.id) {
            setDeliveryStatus(prev => ({ ...prev, [msgWithProfile?.id]: 'delivered' }));
          }
          // Check for @mention
          if (currentUser?.id && msgWithProfile?.sender_id !== currentUser?.id) {
            const { data: profile } = await supabase?.from('user_profiles')?.select('username')?.eq('id', currentUser?.id)?.single();
            if (profile?.username && msgWithProfile?.message_text?.includes(`@${profile?.username}`)) {
              const notif = {
                id: `mention-${Date.now()}`,
                from: msgWithProfile?.user_profiles?.username,
                text: msgWithProfile?.message_text,
                ts: Date.now(),
              };
              setMentionNotifs(prev => [notif, ...prev?.slice(0, 4)]);
              console.log('[FactionChat] @mention notification:', notif);
              setTimeout(() => setMentionNotifs(prev => prev?.filter(n => n?.id !== notif?.id)), 8000);
            }
          }
        }
      })
      ?.on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'faction_activity_feed',
        filter: `faction_id=eq.${userFaction?.id}`,
      }, async (payload) => {
        const { data: actWithProfile } = await supabase?.from('faction_activity_feed')?.select('*, user_profiles(username)')?.eq('id', payload?.new?.id)?.single();
        if (actWithProfile) {
          setActivityFeed((prev) => [actWithProfile, ...prev?.slice(0, 19)]);
        }
      })
      ?.on('system', {}, (status) => {
        if (status === 'SUBSCRIBED') setConnectionStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setConnectionStatus('error');
        else if (status === 'CLOSED') setConnectionStatus('disconnected');
        else setConnectionStatus('connecting');
      })
      ?.subscribe((status) => {
        console.log('[FactionChat] Channel status:', status);
        if (status === 'SUBSCRIBED') setConnectionStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setConnectionStatus('error');
        else if (status === 'CLOSED') setConnectionStatus('disconnected');
      });

    subscriptionRef.current = channel;
    return () => {
      console.log('[FactionChat] Cleaning up message subscription');
      supabase?.removeChannel(channel);
    };
  }, [userFaction?.id, currentUser?.id]);

  // ── Presence channel: online members + typing ─────────────
  useEffect(() => {
    if (!userFaction?.id || !currentUser?.id) return;

    console.log('[FactionChat] Setting up presence channel');

    const presenceChannel = supabase?.channel(`faction-presence-${userFaction?.id}`, {
      config: { presence: { key: currentUser?.id } },
    });

    presenceChannel
      ?.on('presence', { event: 'sync' }, () => {
        const state = presenceChannel?.presenceState();
        const members = {};
        Object.entries(state || {})?.forEach(([key, presences]) => {
          const p = presences?.[0];
          if (p) members[key] = { username: p?.username, online_at: p?.online_at };
        });
        setOnlineMembers(members);
        console.log('[FactionChat] Presence sync — online:', Object.keys(members)?.length);
      })
      ?.on('presence', { event: 'join' }, ({ key, newPresences }) => {
        const p = newPresences?.[0];
        if (p) {
          setOnlineMembers(prev => ({ ...prev, [key]: { username: p?.username, online_at: p?.online_at } }));
        }
      })
      ?.on('presence', { event: 'leave' }, ({ key }) => {
        setOnlineMembers(prev => {
          const next = { ...prev };
          delete next?.[key];
          return next;
        });
        // Remove from typing if they leave
        setTypingUsers(prev => prev?.filter(u => u?.userId !== key));
      })
      ?.on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (!payload || payload?.userId === currentUser?.id) return;
        setTypingUsers(prev => {
          const filtered = prev?.filter(u => u?.userId !== payload?.userId);
          if (payload?.isTyping) {
            return [...filtered, { userId: payload?.userId, username: payload?.username }];
          }
          return filtered;
        });
        // Auto-clear typing after 4 seconds
        setTimeout(() => {
          setTypingUsers(prev => prev?.filter(u => u?.userId !== payload?.userId));
        }, 4000);
      })
      ?.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          const { data: profile } = await supabase?.from('user_profiles')?.select('username')?.eq('id', currentUser?.id)?.single();
          await presenceChannel?.track({
            username: profile?.username || 'Unknown',
            online_at: new Date()?.toISOString(),
          });
          console.log('[FactionChat] Presence tracked for:', profile?.username);
        }
      });

    presenceChannelRef.current = presenceChannel;
    return () => {
      console.log('[FactionChat] Cleaning up presence channel');
      supabase?.removeChannel(presenceChannel);
    };
  }, [userFaction?.id, currentUser?.id]);

  // ── Vault subscription ────────────────────────────────────
  useEffect(() => {
    if (!userFaction?.id) return;

    console.log('[FactionChat] Subscribing to faction vault:', userFaction?.id);

    const vaultChannel = supabase?.channel(`faction-vault-${userFaction?.id}`)
      ?.on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'faction_vaults',
        filter: `faction_id=eq.${userFaction?.id}`,
      }, (payload) => {
        const newBalance = payload?.new?.balance;
        if (newBalance !== undefined) {
          console.log('[FactionChat] Vault balance updated:', newBalance);
          setVaultBalance(newBalance);
          setVaultUpdated(true);
          if (vaultUpdatedTimerRef?.current) clearTimeout(vaultUpdatedTimerRef?.current);
          vaultUpdatedTimerRef.current = setTimeout(() => setVaultUpdated(false), 5000);
        }
      })
      ?.subscribe((status) => {
        console.log('[FactionChat] Vault channel status:', status);
      });

    vaultChannelRef.current = vaultChannel;
    return () => {
      console.log('[FactionChat] Cleaning up vault subscription');
      supabase?.removeChannel(vaultChannel);
      if (vaultUpdatedTimerRef?.current) clearTimeout(vaultUpdatedTimerRef?.current);
    };
  }, [userFaction?.id]);

  // ── Typing broadcast ──────────────────────────────────────
  const broadcastTyping = useCallback(async (isTyping) => {
    if (!presenceChannelRef?.current || !currentUser?.id) return;
    const { data: profile } = await supabase?.from('user_profiles')?.select('username')?.eq('id', currentUser?.id)?.single();
    presenceChannelRef?.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { userId: currentUser?.id, username: profile?.username || 'Unknown', isTyping },
    });
  }, [currentUser?.id]);

  const handleInputChange = useCallback((e) => {
    setNewMessage(e?.target?.value);
    if (!isTypingRef?.current) {
      isTypingRef.current = true;
      broadcastTyping(true);
    }
    if (typingTimerRef?.current) clearTimeout(typingTimerRef?.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      broadcastTyping(false);
    }, 2500);
  }, [broadcastTyping]);

  const sendMessage = async (msgText) => {
    const { error: sendErr } = await supabase?.rpc('send_faction_message', {
      p_faction_id: userFaction?.id,
      p_message_text: msgText,
    });
    return sendErr;
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || sending) return;
    if (!userFaction?.id) return;

    // Stop typing indicator
    if (typingTimerRef?.current) clearTimeout(typingTimerRef?.current);
    isTypingRef.current = false;
    broadcastTyping(false);

    setSending(true);
    setRateLimitMsg('');
    setError(null);

    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      faction_id: userFaction?.id,
      sender_id: currentUser?.id,
      message_text: newMessage?.trim(),
      is_announcement: false,
      created_at: new Date()?.toISOString(),
      user_profiles: { username: 'You' },
      _optimistic: true,
      isPending: true,
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setDeliveryStatus(prev => ({ ...prev, [optimisticId]: 'sent' }));
    const msgText = newMessage?.trim();
    setNewMessage('');

    // Haptic feedback
    if (navigator?.vibrate) navigator.vibrate(50);

    if (!navigator.onLine) {
      await queueMessage({ faction_id: userFaction?.id, message_text: msgText });
      setMessages((prev) => prev?.map(m => m?.id === optimisticId ? { ...m, isPending: false, isFailed: true } : m));
      setDeliveryStatus(prev => { const n = { ...prev }; delete n?.[optimisticId]; return n; });
      setSending(false);
      return;
    }

    try {
      const sendErr = await sendMessage(msgText);
      if (sendErr) {
        setMessages((prev) => prev?.filter((m) => m?.id !== optimisticId));
        setDeliveryStatus(prev => { const n = { ...prev }; delete n?.[optimisticId]; return n; });
        const failedMsg = { id: `failed-${Date.now()}`, message_text: msgText, _failed: true };
        setFailedMessages((prev) => [...prev, failedMsg]);
        if (sendErr?.message?.includes('Rate limit')) {
          setRateLimitMsg('Rate limit: 10 messages per minute');
        } else {
          setError(sendErr?.message);
        }
      } else {
        // Mark as delivered — will be replaced by realtime INSERT
        setDeliveryStatus(prev => ({ ...prev, [optimisticId]: 'delivered' }));
      }
    } catch (err) {
      setMessages((prev) => prev?.filter((m) => m?.id !== optimisticId));
      setDeliveryStatus(prev => { const n = { ...prev }; delete n?.[optimisticId]; return n; });
      const failedMsg = { id: `failed-${Date.now()}`, message_text: msgText, _failed: true };
      setFailedMessages((prev) => [...prev, failedMsg]);
      setError(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleRetryMessage = async (failedMsg) => {
    setFailedMessages((prev) => prev?.filter((m) => m?.id !== failedMsg?.id));
    const optimisticMsg = {
      id: `optimistic-${Date.now()}`,
      faction_id: userFaction?.id,
      sender_id: currentUser?.id,
      message_text: failedMsg?.message_text,
      is_announcement: false,
      created_at: new Date()?.toISOString(),
      user_profiles: { username: 'You' },
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    try {
      const sendErr = await sendMessage(failedMsg?.message_text);
      if (sendErr) {
        setMessages((prev) => prev?.filter((m) => m?.id !== optimisticMsg?.id));
        setFailedMessages((prev) => [...prev, failedMsg]);
        setError(sendErr?.message);
      } else {
        setMessages((prev) => prev?.filter((m) => m?.id !== optimisticMsg?.id));
      }
    } catch (err) {
      setMessages((prev) => prev?.filter((m) => m?.id !== optimisticMsg?.id));
      setFailedMessages((prev) => [...prev, failedMsg]);
      setError(err?.message);
    }
  };

  const handleDismissFailedMessage = (id) => {
    setFailedMessages((prev) => prev?.filter((m) => m?.id !== id));
  };

  const handlePinMessage = async (messageId) => {
    if (!userFaction?.id) return;
    try {
      await supabase?.rpc('pin_announcement', { p_message_id: messageId, p_faction_id: userFaction?.id });
      setMessages((prev) => prev?.map((m) => m?.id === messageId ? { ...m, is_announcement: true, pinned_by_leader: currentUser?.id } : m));
    } catch (err) {
      setError(err?.message || 'Failed to pin message');
    }
  };

  const handleMarkNotifRead = (notifId) => {
    setNotifications((prev) => prev?.map((n) => (n?.id === notifId ? { ...n, read_at: new Date()?.toISOString() } : n)));
  };

  const canPin = ['LEADER', 'OFFICER']?.includes(userRank);
  const pinnedMessages = messages?.filter((m) => m?.is_announcement);
  const regularMessages = messages?.filter((m) => !m?.is_announcement);
  const onlineCount = Object.keys(onlineMembers)?.length;

  const validateMessage = (text) => {
    if (!text?.trim()) return 'Message cannot be empty';
    if (text?.length > 500) return `Message too long (${text?.length}/500 characters)`;
    return '';
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex-1 flex flex-col overflow-hidden" style={{ marginLeft: sidebarCollapsed ? '72px' : '280px' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 lg:px-6 py-4 border-b border-border bg-card/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Icon name="MessageSquare" size={20} className="text-primary" />
            <div>
              <h1 className="font-bold text-foreground">{userFaction?.faction_name || 'Faction Chat'}</h1>
              <p className="text-xs text-muted-foreground">
                {userFaction ? `[${userFaction?.tag}] · ${userRank}` : 'Join a faction to chat'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ConnectionStatus status={connectionStatus} onlineCount={onlineCount} />
            {/* Panel Switcher (mobile) */}
            <div className="flex gap-1 lg:hidden">
              {['chat', 'activity', 'notifications']?.map((panel) => (
                <button
                  key={panel}
                  onClick={() => setActivePanel(panel)}
                  className={`px-2 py-1 rounded text-xs capitalize transition-colors min-h-[36px] ${
                    activePanel === panel ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {panel === 'notifications' ? 'Notifs' : panel}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* @mention notifications */}
        {mentionNotifs?.length > 0 && (
          <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-xs">
            {mentionNotifs?.map(notif => (
              <div key={notif?.id}
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg border shadow-lg"
                style={{ background: 'rgba(124,58,237,0.15)', borderColor: 'rgba(124,58,237,0.4)', color: '#C4B5FD' }}>
                <Icon name="AtSign" size={14} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold">{notif?.from} mentioned you</p>
                  <p className="text-xs opacity-80 truncate">{notif?.text}</p>
                </div>
                <button onClick={() => setMentionNotifs(prev => prev?.filter(n => n?.id !== notif?.id))} className="opacity-60 hover:opacity-100">
                  <Icon name="X" size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!userFaction ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-foreground mb-2">No Faction</h2>
              <p className="text-muted-foreground text-sm">Join or create a faction to access faction chat</p>
            </div>
          </div>
        ) : (
          <ChatErrorBoundary>
            <div className="flex-1 flex overflow-hidden">
              {/* Main Chat Area */}
              <div className={`flex flex-col flex-1 overflow-hidden ${activePanel !== 'chat' ? 'hidden lg:flex' : ''}`}>
                {/* Vault Banner */}
                <VaultBanner vaultBalance={vaultBalance} vaultUpdated={vaultUpdated} />

                {/* Pinned Announcements */}
                {pinnedMessages?.length > 0 && (
                  <div className="px-4 py-2 bg-yellow-500/5 border-b border-yellow-500/20 flex-shrink-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Pin" size={12} className="text-yellow-400" />
                      <span className="text-xs font-semibold text-yellow-400">Pinned Announcements</span>
                    </div>
                    <div className="space-y-1">
                      {pinnedMessages?.slice(0, 2)?.map((msg) => (
                        <div key={msg?.id} className="text-xs text-foreground bg-yellow-500/10 rounded px-2 py-1">
                          <span className="font-medium text-yellow-400">{msg?.user_profiles?.username}:</span>{' '}{msg?.message_text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4]?.map((i) => (
                        <div key={i} className="flex gap-2">
                          <div className="w-7 h-7 rounded-full bg-muted animate-pulse flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <div className="h-3 bg-muted rounded animate-pulse w-24" />
                            <div className="h-8 bg-muted rounded animate-pulse w-48" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : regularMessages?.length === 0 ? (
                    <ChatEmptyState />
                  ) : (
                    regularMessages?.map((msg) => (
                      <div key={msg?.id}>
                        <MessageBubble
                          message={msg}
                          isOwn={msg?.sender_id === currentUser?.id}
                          onPin={handlePinMessage}
                          canPin={canPin}
                        />
                        {/* Delivery status for own messages */}
                        {msg?.sender_id === currentUser?.id && (
                          <div className="flex justify-end pr-1 mt-0.5">
                            {msg?._optimistic && deliveryStatus?.[msg?.id] === 'sent' && (
                              <span className="text-[10px] text-muted-foreground/60">Sent</span>
                            )}
                            {deliveryStatus?.[msg?.id] === 'delivered' && (
                              <span className="text-[10px] text-emerald-400/70">✓✓ Delivered</span>
                            )}
                            {deliveryStatus?.[msg?.id] === 'failed' && (
                              <span className="text-[10px] text-red-400">Failed</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Typing Indicator */}
                <TypingIndicator typingUsers={typingUsers} />

                {/* Failed Messages Queue */}
                {failedMessages?.length > 0 && (
                  <div className="flex-shrink-0 border-t border-red-500/20 bg-red-500/5">
                    {failedMessages?.map((msg) => (
                      <FailedMessageItem
                        key={msg?.id}
                        msg={msg}
                        onRetry={handleRetryMessage}
                        onDismiss={handleDismissFailedMessage}
                      />
                    ))}
                  </div>
                )}

                {/* Error / Rate Limit */}
                {(error || rateLimitMsg) && (
                  <div className="px-4 py-2 flex-shrink-0">
                    <div className="p-2 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon name="AlertCircle" size={12} />
                        {error || rateLimitMsg}
                      </div>
                      <button onClick={() => { setError(null); setRateLimitMsg(''); }} className="text-red-400/60 hover:text-red-400">
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="px-4 py-3 border-t border-border flex-shrink-0">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={handleInputChange}
                      placeholder="Type a message... (10/min limit)"
                      maxLength={500}
                      inputMode="text"
                      className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage?.trim() || sending}
                      className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2 min-h-[44px] min-w-[44px]"
                    >
                      {sending ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Send" size={14} />}
                      <span className="hidden sm:inline">Send</span>
                    </button>
                  </form>
                  <p className="text-xs text-muted-foreground mt-1">{newMessage?.length}/500 characters</p>
                </div>
              </div>

              {/* Right Sidebar: Activity + Notifications + Online Members */}
              <div className={`w-full lg:w-80 xl:w-96 border-l border-border flex flex-col overflow-hidden flex-shrink-0 ${activePanel === 'chat' ? 'hidden lg:flex' : ''}`}>
                {/* Online Members */}
                {onlineCount > 0 && (
                  <div className="px-3 py-2 border-b border-border flex-shrink-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-xs font-semibold text-foreground">{onlineCount} Online</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.values(onlineMembers)?.slice(0, 8)?.map((member, i) => (
                        <OnlineMemberDot key={i} username={member?.username} isOnline />
                      ))}
                      {onlineCount > 8 && (
                        <span className="text-xs text-muted-foreground px-2 py-1">+{onlineCount - 8} more</span>
                      )}
                    </div>
                  </div>
                )}

                <div className={`flex flex-col overflow-hidden ${activePanel === 'notifications' ? 'hidden lg:flex' : ''} flex-1`}>
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0">
                    <Icon name="Activity" size={14} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">Activity Feed</span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-2 py-2">
                    {activityFeed?.length === 0 ? (
                      <div className="p-4 text-center">
                        <Icon name="Activity" size={24} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground">No recent activity</p>
                      </div>
                    ) : (
                      activityFeed?.map((activity) => <ActivityFeedItem key={activity?.id} activity={activity} />)
                    )}
                  </div>
                </div>
                {canPin && (
                  <div className={`flex-shrink-0 border-t border-border ${activePanel === 'activity' ? 'hidden lg:block' : ''}`}>
                    <NotificationPanel notifications={notifications} onMarkRead={handleMarkNotifRead} />
                  </div>
                )}
              </div>
            </div>
          </ChatErrorBoundary>
        )}
      </main>
    </div>
  );
};

export default FactionChat;
